import { Inject, Injectable, InjectionToken } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, from, Observable } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { WorkspaceItem } from '@telespot/domain';
import {
  ListUserMemberships,
  ListUserMembershipsUseCase,
  UserMembership,
} from '@telespot/management/use-cases';
import {
  AddMemberships,
  AddMembershipsError,
  RemoveMemberships,
  RemoveMembershipsError,
} from '@telespot/authentication/use-cases';
import { UserClient } from './users.client';
import { UserMembershipsState } from './user-memberships.state';
import { WorkspaceMemberData } from './workspace-members.state';

export const USER_MEMBERSHIPS_LIST_USE_CASE = new InjectionToken<ListUserMembershipsUseCase>(
  'USER_MEMBERSHIPS_LIST_USE_CASE'
);
export const USER_MEMBERSHIPS_CLIENT = new InjectionToken<UserClient>('USER_MEMBERSHIPS_CLIENT');

const MAX_CONCURRENCY = 2;

@Injectable()
export class UserMembershipsEffects {
  constructor(
    private readonly state: UserMembershipsState,
    @Inject(USER_MEMBERSHIPS_LIST_USE_CASE) private readonly listUserMembershipsUseCase: ListUserMembershipsUseCase,
    @Inject(USER_MEMBERSHIPS_CLIENT) private readonly userClient: UserClient,
    private readonly translateService: TranslateService
  ) { }

  // ============================================================================
  // Selectors — re-exposed from UserMembershipsState
  // ============================================================================

  public get organizationId$() {
    return this.state.organizationId$;
  }
  public get activeUser$() {
    return this.state.activeUser$;
  }
  public get activeUserMemberships$() {
    return this.state.activeUserMemberships$;
  }
  public get membershipsToAdd$() {
    return this.state.membershipsToAdd$;
  }
  public get membershipsToRemove$() {
    return this.state.membershipsToRemove$;
  }
  public get addedToWorkspaces$() {
    return this.state.addedToWorkspaces$;
  }
  public get removedFromWorkspaces$() {
    return this.state.removedFromWorkspaces$;
  }
  public get loadingMemberships$() {
    return this.state.loadingMemberships$;
  }
  public get membershipOperationError$() {
    return this.state.membershipOperationError$;
  }
  public get loadError$() {
    return this.state.loadError$;
  }
  public get activeMembershipsCount$() {
    return this.state.activeMembershipsCount$;
  }
  public get selectedWorkspaces$() {
    return this.state.selectedWorkspaces$;
  }
  public get selectedWorkspacesCount$() {
    return this.state.selectedWorkspacesCount$;
  }

  // ============================================================================
  // Updater aliases (passthrough)
  // ============================================================================

  public readonly setOrganizationId = (organizationId: string) => this.state.setOrganizationId(organizationId);
  public readonly setActiveUser = (params: { activeUser: WorkspaceMemberData; clearMemberships: boolean }) =>
    this.state.setActiveUser(params);
  public readonly addToWorkspaces = (workspaces: WorkspaceItem[]) => this.state.addToWorkspaces(workspaces);
  public readonly removeFromWorkspaces = (workspaces: WorkspaceItem[]) => this.state.removeFromWorkspaces(workspaces);
  public readonly resetMembershipState = () => this.state.resetMembershipState();

  // ============================================================================
  // Custom effects
  // ============================================================================

  public readonly loadUserMemberships = this.state.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.state.activeUser$, this.state.organizationId$]),
      filter(([, activeUser, organizationId]) => !!activeUser?.id && !!organizationId),
      switchMap(([, activeUser, organizationId]) => {
        this.state.setLoadingMemberships(true);
        return from(
          this.listUserMembershipsUseCase.execute(
            new ListUserMemberships({ userId: activeUser?.id ?? '', organizationId: organizationId ?? '' })
          )
        ).pipe(
          tap((memberships) => this.state.setInitialWorkspaces({ memberships })),
          catchError((error: Error) => {
            this.state.setLoadError(error.message);
            return EMPTY;
          })
        );
      })
    )
  );

  public readonly syncWorkspaceChanges = this.state.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.state.activeUser$, this.state.membershipsToAdd$, this.state.membershipsToRemove$]),
      filter(
        ([, , membershipsToAdd, membershipsToRemove]) =>
          membershipsToAdd.length > 0 || membershipsToRemove.length > 0
      ),
      switchMap(([, activeUser, membershipsToAdd, membershipsToRemove]) =>
        from(this.performSyncWorkspaceChanges(activeUser?.id ?? '', membershipsToAdd, membershipsToRemove)).pipe(
          tap(([addedMemberships, removedMembershipIds]) => {
            if (addedMemberships.length > 0) {
              this.state.confirmMembershipsAdded(addedMemberships);
            }
            if (removedMembershipIds.length > 0) {
              this.state.confirmMembershipsRemoved(removedMembershipIds);
            }
          }),
          catchError((error: Error) => {
            this.state.setMembershipOperationError(error.message);
            return EMPTY;
          })
        )
      )
    )
  );

  // ============================================================================
  // Helpers
  // ============================================================================

  private async performSyncWorkspaceChanges(
    userId: string,
    membershipsToAdd: UserMembership[],
    membershipsToRemove: UserMembership[]
  ): Promise<[UserMembership[], string[]]> {
    const results = await Promise.allSettled([
      membershipsToAdd.length > 0 ? this.addUserToWorkspaces(userId, membershipsToAdd) : Promise.resolve([]),
      membershipsToRemove.length > 0 ? this.deleteMembershipsForUser(membershipsToRemove) : Promise.resolve(true),
    ]);

    const addResult = results[0];
    const addedMemberships = addResult.status === 'fulfilled' ? (addResult.value as UserMembership[]) : [];

    const removeResult = results[1];
    const removedMembershipIds =
      removeResult.status === 'fulfilled' && removeResult.value
        ? membershipsToRemove.map((membership) => membership.workspaceId)
        : [];

    return [addedMemberships, removedMembershipIds];
  }

  private async addUserToWorkspaces(userId: string, memberships: UserMembership[]): Promise<UserMembership[]> {
    if (memberships.length === 0) return [];

    try {
      const newAssociations = await this.userClient.send(
        new AddMemberships({
          userId,
          workspaceIds: memberships.map((membership) => membership.workspaceId).filter((id) => id !== undefined),
          batchSize: MAX_CONCURRENCY,
        })
      );

      return memberships.map((membership) => {
        const association = newAssociations.find(
          (item: { workspaceId: string; membershipId: string }) => item.workspaceId === membership.workspaceId
        );
        return { ...membership, membershipId: association?.membershipId };
      });
    } catch (error) {
      this.handleAddMembershipError(error as Error | AddMembershipsError);
      return [];
    }
  }

  private async deleteMembershipsForUser(memberships: UserMembership[]): Promise<boolean> {
    if (memberships.length === 0) return true;

    try {
      await this.userClient.send(
        new RemoveMemberships({
          membershipIds: memberships
            .map((membership) => membership.membershipId)
            .filter((id): id is string => id !== undefined),
          batchSize: MAX_CONCURRENCY,
        })
      );
      return true;
    } catch (error) {
      this.handleRemoveMembershipError(error as Error | RemoveMembershipsError);
      return false;
    }
  }

  private handleAddMembershipError(error: Error | AddMembershipsError) {
    console.error(error);
    this.state.setMembershipOperationError(this.translateService.instant('users.error.add_membership.unknown'));
  }

  private handleRemoveMembershipError(error: Error | RemoveMembershipsError) {
    console.error(error);
    this.state.setMembershipOperationError(this.translateService.instant('users.error.remove_membership.unknown'));
  }
}
